/**
 * Changelog & Impact Notifications Utilities
 */

import {
  Change,
  ChangeType,
  ChangelogEntry,
  ExtendedArchiMateDiagram,
} from './extended-types';
import { ArchiMateElement, ArchiMateRelationship, ArchiMateDiagram } from './types';
import { uuidv7 } from 'uuidv7';

export function detectChanges(
  before: ArchiMateDiagram,
  after: ArchiMateDiagram,
  user?: string
): Change[] {
  const changes: Change[] = [];
  const timestamp = Date.now();

  // Detect element changes
  const beforeElements = new Map(before.elements.map((e) => [e.id, e]));
  const afterElements = new Map(after.elements.map((e) => [e.id, e]));

  // Added elements
  afterElements.forEach((element, id) => {
    if (!beforeElements.has(id)) {
      changes.push({
        id: uuidv7(),
        type: 'element-added',
        timestamp,
        user,
        diagramId: after.id,
        diagramName: after.name,
        elementId: id,
        elementName: element.name,
        after: element,
        impactedTeams: [], // To be filled by analyzing relationships
      });
    }
  });

  // Removed elements
  beforeElements.forEach((element, id) => {
    if (!afterElements.has(id)) {
      changes.push({
        id: uuidv7(),
        type: 'element-removed',
        timestamp,
        user,
        diagramId: after.id,
        diagramName: after.name,
        elementId: id,
        elementName: element.name,
        before: element,
        impactedTeams: [],
      });
    }
  });

  // Modified elements
  afterElements.forEach((afterEl, id) => {
    const beforeEl = beforeElements.get(id);
    if (beforeEl && hasElementChanged(beforeEl, afterEl)) {
      changes.push({
        id: uuidv7(),
        type: 'element-modified',
        timestamp,
        user,
        diagramId: after.id,
        diagramName: after.name,
        elementId: id,
        elementName: afterEl.name,
        before: beforeEl,
        after: afterEl,
        impactedTeams: [],
      });
    }
  });

  // Detect relationship changes
  const beforeRels = new Map(before.relationships.map((r) => [r.id, r]));
  const afterRels = new Map(after.relationships.map((r) => [r.id, r]));

  // Added relationships
  afterRels.forEach((rel, id) => {
    if (!beforeRels.has(id)) {
      changes.push({
        id: uuidv7(),
        type: 'relationship-added',
        timestamp,
        user,
        diagramId: after.id,
        diagramName: after.name,
        relationshipId: id,
        after: rel,
        impactedTeams: [],
      });
    }
  });

  // Removed relationships
  beforeRels.forEach((rel, id) => {
    if (!afterRels.has(id)) {
      changes.push({
        id: uuidv7(),
        type: 'relationship-removed',
        timestamp,
        user,
        diagramId: after.id,
        diagramName: after.name,
        relationshipId: id,
        before: rel,
        impactedTeams: [],
      });
    }
  });

  return changes;
}

function hasElementChanged(before: ArchiMateElement, after: ArchiMateElement): boolean {
  return (
    before.name !== after.name ||
    before.type !== after.type ||
    before.layer !== after.layer ||
    before.documentation !== after.documentation ||
    JSON.stringify(before.properties) !== JSON.stringify(after.properties)
  );
}

export function createChangelogEntry(
  date: string,
  changes: Change[],
  author?: string
): ChangelogEntry {
  const summary = generateChangeSummary(changes);

  return {
    id: uuidv7(),
    date,
    changes,
    summary,
    author,
  };
}

function generateChangeSummary(changes: Change[]): string {
  const counts = {
    'element-added': 0,
    'element-removed': 0,
    'element-modified': 0,
    'relationship-added': 0,
    'relationship-removed': 0,
    'relationship-modified': 0,
    'diagram-created': 0,
    'diagram-deleted': 0,
    'diagram-renamed': 0,
  };

  changes.forEach((change) => {
    counts[change.type]++;
  });

  const parts: string[] = [];
  if (counts['element-added'] > 0) parts.push(`${counts['element-added']} elements added`);
  if (counts['element-removed'] > 0) parts.push(`${counts['element-removed']} elements removed`);
  if (counts['element-modified'] > 0) parts.push(`${counts['element-modified']} elements modified`);
  if (counts['relationship-added'] > 0)
    parts.push(`${counts['relationship-added']} relationships added`);
  if (counts['relationship-removed'] > 0)
    parts.push(`${counts['relationship-removed']} relationships removed`);

  return parts.join(', ');
}

export function getImpactedTeams(
  change: Change,
  diagram: ExtendedArchiMateDiagram
): string[] {
  const teams = new Set<string>();

  // Get team for the changed element
  if (change.elementId) {
    const ownership = diagram.elementMetadata[change.elementId]?.ownership;
    if (ownership) teams.add(ownership.team);
  }

  // Get teams for connected elements
  if (change.relationshipId) {
    const rel = diagram.relationships.find((r) => r.id === change.relationshipId);
    if (rel) {
      const sourceOwnership = diagram.elementMetadata[rel.sourceId]?.ownership;
      const targetOwnership = diagram.elementMetadata[rel.targetId]?.ownership;
      if (sourceOwnership) teams.add(sourceOwnership.team);
      if (targetOwnership) teams.add(targetOwnership.team);
    }
  }

  return Array.from(teams);
}

export function enrichChangesWithImpact(
  changes: Change[],
  diagram: ExtendedArchiMateDiagram
): Change[] {
  return changes.map((change) => ({
    ...change,
    impactedTeams: getImpactedTeams(change, diagram),
  }));
}

export function generateChangeNotification(change: Change): string {
  let notification = `🚨 **Architecture Change Alert**\n\n`;
  notification += `**Diagram:** ${change.diagramName}\n`;
  notification += `**Type:** ${formatChangeType(change.type)}\n`;
  notification += `**Time:** ${new Date(change.timestamp).toLocaleString()}\n`;
  if (change.user) notification += `**Changed by:** ${change.user}\n`;

  notification += `\n**Details:**\n`;

  switch (change.type) {
    case 'element-added':
      notification += `➕ Added element: ${change.elementName}\n`;
      break;
    case 'element-removed':
      notification += `➖ Removed element: ${change.elementName}\n`;
      break;
    case 'element-modified':
      notification += `✏️ Modified element: ${change.elementName}\n`;
      notification += describeElementChanges(change.before, change.after);
      break;
    case 'relationship-added':
    case 'relationship-removed':
      notification += describeRelationshipChange(change);
      break;
  }

  if (change.impactedTeams.length > 0) {
    notification += `\n**Impacted teams:** ${change.impactedTeams.join(', ')}\n`;
  }

  return notification;
}

function formatChangeType(type: ChangeType): string {
  return type
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function describeElementChanges(before: any, after: any): string {
  const changes: string[] = [];

  if (before.name !== after.name) {
    changes.push(`- Name: "${before.name}" → "${after.name}"`);
  }
  if (before.type !== after.type) {
    changes.push(`- Type: ${before.type} → ${after.type}`);
  }
  if (before.layer !== after.layer) {
    changes.push(`- Layer: ${before.layer} → ${after.layer}`);
  }

  return changes.join('\n');
}

function describeRelationshipChange(change: Change): string {
  const rel = (change.after || change.before) as ArchiMateRelationship;
  const action = change.type === 'relationship-added' ? '➕ Added' : '➖ Removed';
  return `${action} ${rel.type} relationship`;
}

export function generateChangelogMarkdown(entries: ChangelogEntry[]): string {
  let md = `# Architecture Changelog\n\n`;

  entries.forEach((entry) => {
    md += `## ${entry.date}\n\n`;
    if (entry.author) md += `**Author:** ${entry.author}\n\n`;
    md += `**Summary:** ${entry.summary}\n\n`;

    if (entry.changes.length > 0) {
      md += `### Changes\n\n`;
      entry.changes.forEach((change) => {
        md += `- ${formatChangeType(change.type)}: `;
        md += change.elementName || change.relationshipId || 'unknown';
        md += `\n`;
      });
      md += `\n`;
    }
  });

  return md;
}

export function filterChangesByType(changes: Change[], type: ChangeType): Change[] {
  return changes.filter((change) => change.type === type);
}

export function filterChangesByTeam(changes: Change[], team: string): Change[] {
  return changes.filter((change) => change.impactedTeams.includes(team));
}

export function groupChangesByDate(changes: Change[]): Map<string, Change[]> {
  const grouped = new Map<string, Change[]>();

  changes.forEach((change) => {
    const date = new Date(change.timestamp).toISOString().split('T')[0];
    if (!grouped.has(date)) {
      grouped.set(date, []);
    }
    grouped.get(date)!.push(change);
  });

  return grouped;
}

export function getRecentChanges(diagram: ExtendedArchiMateDiagram, days: number = 7): Change[] {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const allChanges = diagram.changelog.flatMap((entry) => entry.changes);
  return allChanges.filter((change) => change.timestamp >= cutoff);
}

export function generateWeeklyDigest(
  diagram: ExtendedArchiMateDiagram,
  weekStart: Date
): string {
  const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

  const changes = diagram.changelog
    .flatMap((entry) => entry.changes)
    .filter((change) => {
      const changeDate = new Date(change.timestamp);
      return changeDate >= weekStart && changeDate < weekEnd;
    });

  if (changes.length === 0) {
    return `# Weekly Architecture Digest\n\n**Week of ${weekStart.toISOString().split('T')[0]}**\n\nNo changes this week.`;
  }

  let digest = `# Weekly Architecture Digest\n\n`;
  digest += `**Week of ${weekStart.toISOString().split('T')[0]}**\n\n`;
  digest += `**Total changes:** ${changes.length}\n\n`;

  // Group by type
  const byType = new Map<ChangeType, Change[]>();
  changes.forEach((change) => {
    if (!byType.has(change.type)) {
      byType.set(change.type, []);
    }
    byType.get(change.type)!.push(change);
  });

  byType.forEach((changes, type) => {
    digest += `### ${formatChangeType(type)} (${changes.length})\n\n`;
    changes.forEach((change) => {
      digest += `- ${change.elementName || change.relationshipId || 'unknown'}`;
      if (change.user) digest += ` (by ${change.user})`;
      digest += `\n`;
    });
    digest += `\n`;
  });

  // Impacted teams
  const impactedTeams = new Set<string>();
  changes.forEach((change) => {
    change.impactedTeams.forEach((team) => impactedTeams.add(team));
  });

  if (impactedTeams.size > 0) {
    digest += `### Impacted Teams\n\n`;
    Array.from(impactedTeams).forEach((team) => {
      const teamChanges = filterChangesByTeam(changes, team);
      digest += `- **${team}**: ${teamChanges.length} changes\n`;
    });
  }

  return digest;
}

export function compareVersions(
  v1: ArchiMateDiagram,
  v2: ArchiMateDiagram
): {
  added: ArchiMateElement[];
  removed: ArchiMateElement[];
  modified: Array<{ before: ArchiMateElement; after: ArchiMateElement }>;
  relationshipsAdded: ArchiMateRelationship[];
  relationshipsRemoved: ArchiMateRelationship[];
} {
  const v1Elements = new Map(v1.elements.map((e) => [e.id, e]));
  const v2Elements = new Map(v2.elements.map((e) => [e.id, e]));

  const added: ArchiMateElement[] = [];
  const removed: ArchiMateElement[] = [];
  const modified: Array<{ before: ArchiMateElement; after: ArchiMateElement }> = [];

  v2Elements.forEach((element, id) => {
    if (!v1Elements.has(id)) {
      added.push(element);
    } else {
      const before = v1Elements.get(id)!;
      if (hasElementChanged(before, element)) {
        modified.push({ before, after: element });
      }
    }
  });

  v1Elements.forEach((element, id) => {
    if (!v2Elements.has(id)) {
      removed.push(element);
    }
  });

  const v1Rels = new Map(v1.relationships.map((r) => [r.id, r]));
  const v2Rels = new Map(v2.relationships.map((r) => [r.id, r]));

  const relationshipsAdded: ArchiMateRelationship[] = [];
  const relationshipsRemoved: ArchiMateRelationship[] = [];

  v2Rels.forEach((rel, id) => {
    if (!v1Rels.has(id)) {
      relationshipsAdded.push(rel);
    }
  });

  v1Rels.forEach((rel, id) => {
    if (!v2Rels.has(id)) {
      relationshipsRemoved.push(rel);
    }
  });

  return { added, removed, modified, relationshipsAdded, relationshipsRemoved };
}

export function exportChangelogToCSV(diagram: ExtendedArchiMateDiagram): string {
  let csv = 'Date,Type,Element,User,Teams,Message\n';

  diagram.changelog.forEach((entry) => {
    entry.changes.forEach((change) => {
      const date = new Date(change.timestamp).toISOString().split('T')[0];
      const elementName = change.elementName || change.relationshipId || '';
      const teams = change.impactedTeams.join(';');
      const message = (change.message || '').replace(/"/g, '""');

      csv += `"${date}","${change.type}","${elementName}","${change.user || ''}","${teams}","${message}"\n`;
    });
  });

  return csv;
}

export function getChangelogByUser(diagram: ExtendedArchiMateDiagram, user: string): Change[] {
  const allChanges = diagram.changelog.flatMap((entry) => entry.changes);
  return allChanges.filter((change) => change.user === user);
}

// Aliases for backward compatibility
export const addChangelogEntry = createChangelogEntry;
export const getChangelogByType = filterChangesByType;
export const getChangelogSince = getRecentChanges;
